/* eslint-disable no-plusplus */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { UserDetailsContext } from '@/modules/RegisterLayout/contexts/UserDetailsContextProvider';
import { useRouter } from 'next/router';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { Dropdown, InputField, Checkbox, Typography, ToggleButton } from '@pw-tech/omni-ui';
import CourseSelection from '@/modules/CourseSelection/components/CourseSelection/CourseSelection';
import { PATTERN } from '@/modules/common/constants';
import StatesAndCities from '@/modules/common/statesAndCities';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { getNextStep } from '@/modules/common/utils';
import DialogModal from '@/modules/common/components/Modal/DialogModal';
import Image from 'next/image';
import InitialDetails from './InitialDetails/InitialDetails';
import CompetitiveExamDetails from './CompetitiveExamDetails/CompetitiveExamDetails';
import useIsEligibleForDirectAdmission from '@/modules/RegisterLayout/hooks/useIsEligibleForDirectAdmission';
import { dataLayer } from '@/utils/dataLayer';
import AdmissionClosedModal from '@/modules/RegisterLayout/Modals/AdmissionClosedModal/AdmissionClosedModal';
import { WHO_ARE_YOU } from '@/modules/common/enums';
const DEGREE_TYPE_OPTIONS = [{
  label: 'B.Tech',
  value: 'B.Tech'
}, {
  label: 'B.E.',
  value: 'B.E.'
}, {
  label: 'B.Com',
  value: 'B.Com'
}, {
  label: 'B.B.A',
  value: 'B.B.A'
}, {
  label: 'B.Sc',
  value: 'B.Sc'
}, {
  label: 'B.A',
  value: 'B.A'
}, {
  label: 'B.Arch',
  value: 'B.Arch'
}, {
  label: 'Others',
  value: 'Others'
}];
const PASSING_MONTH_OPTIONS = [{
  label: 'January',
  value: 'January'
}, {
  label: 'February',
  value: 'February'
}, {
  label: 'March',
  value: 'March'
}, {
  label: 'April',
  value: 'April'
}, {
  label: 'May',
  value: 'May'
}, {
  label: 'June',
  value: 'June'
}, {
  label: 'July',
  value: 'July'
}, {
  label: 'August',
  value: 'August'
}, {
  label: 'September',
  value: 'September'
}, {
  label: 'October',
  value: 'October'
}, {
  label: 'November',
  value: 'November'
}, {
  label: 'December',
  value: 'December'
}];
const MONTH_NAME_TO_NUMBER: Record<string, number> = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12
};
const NUMBER_TO_MONTH_NAME: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
};
const getPassingYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= currentYear - 10; year--) {
    years.push({
      label: String(year),
      value: String(year)
    });
  }
  return years;
};
const BasicDetailsForm = () => {
  const [showCourseSelectionDialog, setShowCourseSelectionDialog] = useState(false);
  const [showUnEligibleDialog, setShowUnEligibleDialog] = useState(false);
  const [compititionExam, setCompetitionExam] = useState<any>(null);
  const [showAdmissionClosedDialog, setShowAdmissionClosedDialog] = useState(false);
  const [isDeclarationChecked, setIsDeclarationChecked] = useState(false);
  const {
    courses,
    selectedCourseData,
    setSelectedCourseData
  } = useContext(CourseContext);
  const {
    registrationDetails,
    setRegistrationDetails,
    isFormSubmitting,
    submitRegisterForm
  } = useContext(RegistrationContext);
  // const [lockEmailChange, setLockEmailChange] = useState(() =>
  //   Boolean(registrationDetails?.tenthPercentage)
  // );
  const {
    data: isEligibleForDirectAdmission,
    refetch: refetchEligibility,
    isLoading: isLoadingEligibility,
    isFetching: isFetchingEligibility
  } = useIsEligibleForDirectAdmission({
    enabled: false
  });
  const {
    user
  } = useContext(UserDetailsContext);
  const router = useRouter();
  const {
    courseId,
    step,
    coming_from_page,
    section
  } = router.query;
  const utmQuery = JSON.parse(sessionStorage.getItem('query') || '{}');

  // Moving campus state initialization outside of the handler
  const initialCampus = useMemo(() => {
    return courses?.find((course: any) => course._id === registrationDetails?.courseId)?.campus || [];
  }, [courses, registrationDetails?.courseId]);
  const [campus, setCampus] = useState(initialCampus);

  // Memoize defaultValues to prevent unnecessary recalculations
  const defaultValues = useMemo((): any => {
    // User’s previously ticked exams
    const validExams = registrationDetails?.competitiveExams?.filter((exam: any) => exam.name);
    const selectedCompetitiveExams = validExams?.length ? validExams.map((exam: any) => ({
      name: exam.name || '',
      score: exam.score || '',
      nestedKeyValue: exam.nestedKeyValue || ''
    })) : [{
      name: '',
      score: '',
      nestedKeyValue: ''
    }];
    setCompetitionExam(selectedCourseData?.competitiveExams);
    setIsDeclarationChecked(registrationDetails?.step > 1 || registrationDetails?.isPaid);
    const updatedValues = {
      whoAreYou: registrationDetails?.whoAreYou || '',
      fullName: registrationDetails?.fullName ? `${registrationDetails?.fullName || ''}`.trim() : `${user.firstName} ${user.lastName}`.trim(),
      email: registrationDetails?.email || user?.email || '',
      gender: registrationDetails?.gender || '',
      courseId: selectedCourseData?._id ? selectedCourseData?._id : registrationDetails?.courseId || '',
      batchId: registrationDetails?.batchId || selectedCourseData?.batch?._id || '',
      whatsappNumber: registrationDetails?.whatsappNumber || '',
      phoneNumber: registrationDetails?.phoneNumber || user?.phoneNumber || user?.primaryNumber || '',
      studentPhoneNumber: registrationDetails?.studentPhoneNumber || '',
      state: registrationDetails?.state || '',
      city: registrationDetails?.city || '',
      tenthPercentage: registrationDetails?.tenthPercentage || '',
      campusId: registrationDetails?.campusId || '',
      campusId2: registrationDetails?.campusId2 || '',
      tenthResult: registrationDetails?.tenthResult || '',
      twelthPercentage: registrationDetails?.twelthPercentage || '',
      twelfthResult: registrationDetails?.twelfthResult || '',
      twelfthAdmitCard: registrationDetails?.twelfthAdmitCard || '',
      competitiveExams: selectedCompetitiveExams,
      isEws: registrationDetails?.isEws === true ? 'Yes' : registrationDetails?.isEws === false ? 'No' : '',
      ewsCertificate: registrationDetails?.ewsCertificate || '',
      competitiveExamDocument: registrationDetails?.competitiveExamDocument || '',
      ...(registrationDetails?.degreeType ? {
        degreeType: registrationDetails.degreeType
      } : {}),
      ...(registrationDetails?.otherDegree ? {
        otherDegree: registrationDetails.otherDegree
      } : {}),
      ...(registrationDetails?.collegeName ? {
        collegeName: registrationDetails.collegeName
      } : {}),
      ...(registrationDetails?.aggregatePercentage ? {
        aggregatePercentage: String(registrationDetails.aggregatePercentage)
      } : {}),
      ...(registrationDetails?.passingMonth ? {
        passingMonth: NUMBER_TO_MONTH_NAME[registrationDetails.passingMonth] || String(registrationDetails.passingMonth)
      } : {}),
      ...(registrationDetails?.passingYear ? {
        passingYear: String(registrationDetails.passingYear)
      } : {}),
      ...(registrationDetails?.hasWorkExperience !== undefined ? {
        hasWorkExperience: registrationDetails.hasWorkExperience
      } : {}),
      ...(registrationDetails?.organizationName ? {
        organizationName: registrationDetails.organizationName
      } : {}),
      ...(registrationDetails?.designation ? {
        designation: registrationDetails.designation
      } : {}),
      ...(registrationDetails?.totalExperienceYears ? {
        totalExperienceYears: String(registrationDetails.totalExperienceYears)
      } : {}),
      ...(registrationDetails?.annualCtc ? {
        annualCtc: String(registrationDetails.annualCtc)
      } : {})
    };
    return updatedValues;
  }, [registrationDetails]);
  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues
  });
  const {
    setValue,
    formState: {
      isValid,
      errors,
      isSubmitted,
      touchedFields
    },
    handleSubmit,
    watch,
    reset,
    control
  } = methods;
  const shouldShowError = (fieldName: any) => {
    return (isSubmitted || touchedFields[fieldName]) && errors[fieldName]?.message;
  };
  const stateValue = useWatch({
    control,
    name: 'state'
  });
  const tenthPercentageValue = useWatch({
    control,
    name: 'tenthPercentage'
  });
  const whatsappNumberValue = useWatch({
    control,
    name: 'whatsappNumber'
  });
  const phoneNumberValue = useWatch({
    control,
    name: 'phoneNumber'
  });
  const whoAreYouValue = useWatch({
    control,
    name: 'whoAreYou'
  });
  const courseIdValue = useWatch({
    control,
    name: 'courseId'
  });
  const campusIdValue = useWatch({
    control,
    name: 'campusId'
  });
  const hasWorkExperienceValue = useWatch({
    control,
    name: 'hasWorkExperience'
  });
  const degreeTypeValue = useWatch({
    control,
    name: 'degreeType'
  });

  // Updated courseId when after Course Validation Modal
  useEffect(() => {
    if (selectedCourseData?._id && registrationDetails) {
      const updatedFormData = {
        ...defaultValues,
        courseId: selectedCourseData?._id,
        batchId: selectedCourseData?.batch?._id || '',
        campusId: registrationDetails?.campusId || '',
        campusId2: registrationDetails?.campusId2 || ''
      };
      reset(updatedFormData);
    }
  }, [registrationDetails, defaultValues]);

  // useEffect(() => {
  //   if (registrationDetails?.tenthPercentage) {
  //     setLockEmailChange(true);
  //   }
  // }, [registrationDetails?.tenthPercentage]);

  const onSubmit = handleSubmit(async (data: any) => {
    if (!registrationDetails?.tenthPercentage) {
      // We want tenth and twelfth data first time user click on continue btn
      await submitRegisterForm(data, false);
      // setLockEmailChange(true);
    } else {
      const payload = {
        ...data,
        isEws: data?.isEws === 'Yes' ? true : data?.isEws === 'No' ? false : data?.isEws,
        competitiveExams: data?.competitiveExams?.filter((cm: any) => cm?.name && cm.name.trim())?.map((cm: any) => ({
          name: cm.name,
          ...(cm.score !== undefined && cm.score !== null && String(cm.score).trim() !== '' ? {
            score: +cm.score
          } : {}),
          ...(cm.nestedKeyValue ? {
            nestedKeyValue: cm.nestedKeyValue
          } : {})
        })) ?? [],
        ...(data?.passingMonth ? {
          passingMonth: MONTH_NAME_TO_NUMBER[data.passingMonth] || Number(data.passingMonth)
        } : {}),
        ...(data?.passingYear ? {
          passingYear: Number(data.passingYear)
        } : {}),
        ...(data?.aggregatePercentage ? {
          aggregatePercentage: Number(data.aggregatePercentage)
        } : {}),
        ...(data?.hasWorkExperience !== undefined ? {
          hasWorkExperience: !!data.hasWorkExperience
        } : {}),
        ...(data?.hasWorkExperience && data?.organizationName ? {
          organizationName: data.organizationName
        } : {}),
        ...(data?.hasWorkExperience && data?.designation ? {
          designation: data.designation
        } : {}),
        ...(data?.hasWorkExperience && data?.totalExperienceYears ? {
          totalExperienceYears: Number(data.totalExperienceYears)
        } : {}),
        ...(data?.hasWorkExperience && data?.annualCtc ? {
          annualCtc: Number(data.annualCtc)
        } : {}),
        ...(data?.degreeType === 'Others' && data?.otherDegree ? {
          otherDegree: data.otherDegree
        } : {}),
        step: 1
      };
      await submitRegisterForm(payload, false);
      // setLockEmailChange(true);

      const isAdmissionOpen = selectedCourseData?.isCurrent;
      if (isAdmissionOpen === false) {
        setShowAdmissionClosedDialog(true);
        return;
      }
      const skipCee = selectedCourseData?.skipCeeExam;
      if (skipCee) {
        // MBA courses skip eligibility check — proceed directly
        dataLayer({
          event: 'ioi_step_one_continue',
          coming_from_page: coming_from_page ?? '',
          section: section ?? '',
          course_selected: selectedCourseData?.name ?? '',
          ...utmQuery
        });
        await submitRegisterForm({
          ...payload,
          step: getNextStep(router.query.step, registrationDetails?.step)
        }, true);
        return;
      }

      // Check eligibility after successful registration update
      const eligibility = await refetchEligibility();
      dataLayer({
        event: 'ioi_step_one_continue',
        coming_from_page: coming_from_page ?? '',
        section: section ?? '',
        course_selected: selectedCourseData?.name ?? '',
        ...utmQuery
      });
      if (!eligibility?.data?.data?.isEligibleForAdmission) {
        setShowUnEligibleDialog(true);
      } else {
        await submitRegisterForm({
          ...payload,
          step: getNextStep(router.query.step, registrationDetails?.step)
        }, true);
      }
    }
  });
  const getStateOptions = useMemo(() => {
    return Object.keys(StatesAndCities).map((state: string) => ({
      label: state,
      value: state
    }));
  }, []);
  const getCityOptions = useMemo(() => {
    if (stateValue && stateValue in StatesAndCities) {
      return (StatesAndCities[stateValue as keyof typeof StatesAndCities] || []).map((city: string) => ({
        label: city,
        value: city
      }));
    }
    return [];
  }, [stateValue]);
  const getCampusOptions = useMemo(() => {
    if (courses?.length) {
      const selectedCourse: any = courses?.find((course: any) => course._id === courseIdValue);
      return selectedCourse?.campus?.map((camp: any) => ({
        label: camp.name,
        value: camp._id
      }));
    }
    return [];
  }, [courseIdValue, courses]);

  // Filter out selected campusId from campusId2 options
  const getCampusOptions2 = useMemo(() => {
    return getCampusOptions?.filter((campus: any) => campus.value !== campusIdValue);
  }, [getCampusOptions, campusIdValue]);
  const handleDependencyReset = (value: any) => {
    setValue('campusId', '');
    setValue('campusId2', '');
    const selectedCourse: any = courses?.find((course: any) => course._id === value);
    setValue('courseId', selectedCourse?._id, {
      shouldValidate: true
    });
    setValue('batchId', selectedCourse?.batch?._id);
    setCampus(selectedCourse?.campus?.length ? selectedCourse?.campus : []);
    setValue('campusId', '', {
      shouldValidate: true
    });
    setValue('campusId2', '', {
      shouldValidate: true
    });
    setValue('competitiveExams', selectedCourse?.competitiveExams?.length > 0 ? [{
      name: '',
      score: ''
    }] : [], {
      shouldValidate: true
    });
    setSelectedCourseData(selectedCourse);
    if (selectedCourse?.competitiveExams?.length) {
      setCompetitionExam(selectedCourse?.competitiveExams);
    } else {
      setCompetitionExam(null);
    }
  };
  const handleCourseSelection = (data?: any) => {
    if (data?._id) {
      setShowCourseSelectionDialog(false);
      handleDependencyReset(data?._id);
    }
  };
  return <section data-sentry-component="BasicDetailsForm" data-sentry-source-file="index.tsx">
      <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="index.tsx">
        <form className="flex flex-col justify-between h-full pt-2" onSubmit={onSubmit} autoComplete="off" id="basic-details-form">
          <div className="flex flex-col h-full gap-4 my-4">
            <InitialDetails data-sentry-element="InitialDetails" data-sentry-source-file="index.tsx" />
            {registrationDetails?.tenthPercentage ? <>
                {/* Course & Gender */}
                <div className="flex flex-col md:!flex-row gap-4">
                  <div className="w-full">
                    <Controller name="courseId" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please select course of interest'
                  }
                }} render={({
                  field: {
                    value,
                    onChange,
                    ...hookFormField
                  }
                }) => <Dropdown {...hookFormField} label="Course of Interest" placeholder="Course of Interest" options={courses?.map((course: any) => {
                  return {
                    label: course.name,
                    value: course._id
                  };
                })} showOptions={false} fullWidth values={[value]} onClick={event => {
                  setShowCourseSelectionDialog(true);
                }} error={shouldShowError('courseId')} message={shouldShowError('courseId') ? errors.courseId?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                  </div>
                  <div className="w-full">
                    <Controller name="gender" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please select your gender'
                  }
                }} render={({
                  field
                }) => <Dropdown {...field} label="Gender" placeholder="Select Gender" options={[{
                  value: 'Male',
                  label: 'Male'
                }, {
                  value: 'Female',
                  label: 'Female'
                }, {
                  value: 'Other',
                  label: 'Other'
                }]} fullWidth error={shouldShowError('gender')} message={shouldShowError('gender') ? errors.gender?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                  </div>
                </div>

                {/* MBA Specific: Degree Type, College Name, Aggregate %, Passing Month & Year */}
                {selectedCourseData?.skipCeeExam && <>
                    <Typography variant="heading4" weight="semi-bold">
                      Undergraduate Details
                    </Typography>
                    <div className="flex flex-col md:!flex-row gap-4">
                      <div className="w-full">
                        <Controller name="degreeType" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please select degree type'
                    }
                  }} render={({
                    field
                  }) => <Dropdown {...field} label="Degree Type" placeholder="Select Degree Type" options={DEGREE_TYPE_OPTIONS} fullWidth onChange={(e: any) => {
                    field.onChange(e);
                    if (e?.target?.value !== 'Others') {
                      setValue('otherDegree', '', {
                        shouldValidate: true
                      });
                    }
                  }} error={shouldShowError('degreeType')} message={shouldShowError('degreeType') ? errors.degreeType?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                      </div>
                      <div className="w-full">
                        <Controller name="collegeName" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please enter college name'
                    },
                    minLength: {
                      value: 2,
                      message: 'Minimum 2 characters required'
                    }
                  }} render={({
                    field
                  }) => <InputField {...field} label="College Name" placeholder="Enter College Name" required error={shouldShowError('collegeName')} message={shouldShowError('collegeName') ? errors.collegeName?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                      </div>
                    </div>

                    {degreeTypeValue === 'Others' && <div className="flex flex-col md:!flex-row gap-4">
                        <div className="w-full md:!w-1/2">
                          <Controller name="otherDegree" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please specify your degree'
                    },
                    minLength: {
                      value: 2,
                      message: 'Minimum 2 characters required'
                    }
                  }} render={({
                    field
                  }) => <InputField {...field} label="Specify Degree" placeholder="Enter Degree Name" required error={shouldShowError('otherDegree')} message={shouldShowError('otherDegree') ? errors.otherDegree?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                        </div>
                      </div>}

                    <div className="flex flex-col md:!flex-row gap-4">
                      <div className="w-full">
                        <Controller name="aggregatePercentage" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please enter aggregate percentage'
                    },
                    pattern: {
                      value: new RegExp(PATTERN.percentage),
                      message: 'Please enter a valid percentage (0-100, up to 2 decimals)'
                    }
                  }} render={({
                    field
                  }) => <InputField {...field} step="0.01" min="0" max="100" label="Aggregate Percentage" placeholder="Aggregate Percentage" required error={shouldShowError('aggregatePercentage')} message={shouldShowError('aggregatePercentage') ? errors.aggregatePercentage?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                      </div>
                      <div className="w-full">
                        <Controller name="passingMonth" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please select passing month'
                    }
                  }} render={({
                    field
                  }) => <Dropdown {...field} label="Passing Month" placeholder="Select Passing Month" options={PASSING_MONTH_OPTIONS} fullWidth error={shouldShowError('passingMonth')} message={shouldShowError('passingMonth') ? errors.passingMonth?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                      </div>
                      <div className="w-full">
                        <Controller name="passingYear" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please select passing year'
                    }
                  }} render={({
                    field
                  }) => <Dropdown {...field} label="Passing Year" placeholder="Select Passing Year" options={getPassingYearOptions()} fullWidth error={shouldShowError('passingYear')} message={shouldShowError('passingYear') ? errors.passingYear?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                      </div>
                    </div>
                  </>}

                {/* State & City */}
                <div className="flex flex-col md:!flex-row gap-4">
                  <div className="w-full">
                    <Controller name="state" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please select state'
                  }
                }} render={({
                  field
                }) => <Dropdown {...field} label="State" placeholder="State *" options={getStateOptions} fullWidth searchable onChange={event => {
                  field.onChange(event?.target?.value);
                  setValue('city', '');
                }} error={shouldShowError('state')} message={shouldShowError('state') ? errors.state?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                  </div>
                  <div className="w-full">
                    <Controller name="city" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please select city'
                  }
                }} render={({
                  field
                }) => <Dropdown {...field} label="City" placeholder="City *" options={getCityOptions} fullWidth searchable error={shouldShowError('city')} message={shouldShowError('city') ? errors.city?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                  </div>
                </div>

                {/* EWS & Campus */}
                <div className="grid grid-cols-1 md:!grid-cols-2 gap-4">
                  <div className="w-full">
                    <Controller name="campusId" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please select preferred campus'
                  }
                }} render={({
                  field: {
                    value,
                    ...hookFormField
                  }
                }) => <Dropdown {...hookFormField} values={[value]} label="Preferred Campus" placeholder="Select Preferred Campus" options={getCampusOptions} fullWidth error={shouldShowError('campusId')} message={shouldShowError('campusId') ? errors.campusId?.message as string : ''} disabled={registrationDetails?.isPaid} />} />
                  </div>
                  {getCampusOptions?.length > 1 && campusIdValue ? <div className="w-full">
                      <Controller name="campusId2" control={control} render={({
                  field: {
                    value,
                    ...hookFormField
                  }
                }) => <Dropdown {...hookFormField} values={[value]} label="Preferred Campus 2" placeholder="Select Preferred Campus" options={getCampusOptions2} fullWidth disabled={registrationDetails?.isPaid} />} />
                    </div> : null}
                  <div className="w-full">
                    <Controller name="isEws" control={control} render={({
                  field
                }) => <Dropdown {...field} label="EWS Applicable ?" placeholder="EWS Applicable ?" options={[{
                  value: 'Yes',
                  label: 'Yes'
                }, {
                  value: 'No',
                  label: 'No'
                }]} fullWidth disabled={registrationDetails?.isPaid} />} />
                  </div>
                </div>
                {/* Phone & Whatsapp Number */}
                <div className="grid grid-cols-1 md:!grid-cols-2 gap-4">
                  <div className="w-full">
                    <Controller name="phoneNumber" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please enter the mobile number'
                  },
                  pattern: {
                    value: new RegExp(PATTERN.phone),
                    message: 'Please enter the valid mobile number'
                  },
                  minLength: {
                    value: 10,
                    message: 'Minimum 10 digits'
                  },
                  maxLength: {
                    value: 10,
                    message: 'Maximum 10 digits'
                  }
                }} render={({
                  field
                }) => <InputField {...field} type="number" label="Phone Number *" placeholder="Phone Number" error={shouldShowError('phoneNumber')} message={shouldShowError('phoneNumber') ? errors.phoneNumber?.message as string : ''} disabled fullWidth />} />
                  </div>
                  <div className="w-full">
                    <Controller name="whatsappNumber" control={control} rules={{
                  required: {
                    value: true,
                    message: 'Please enter the whatsapp mobile number'
                  },
                  pattern: {
                    value: new RegExp(PATTERN.phone),
                    message: 'Please enter the valid whatsapp mobile number'
                  },
                  minLength: {
                    value: 10,
                    message: 'Minimum 10 digits'
                  },
                  maxLength: {
                    value: 10,
                    message: 'Maximum 10 digits'
                  }
                }} render={({
                  field
                }) => <InputField {...field} type="number" label="Whatsapp Number *" placeholder="Whatsapp Number" error={shouldShowError('whatsappNumber')} message={shouldShowError('whatsappNumber') ? errors.whatsappNumber?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth maxLength={10} />} />
                  </div>
                  {/* Student Phone Number - Only show when Guardian is selected */}
                  {whoAreYouValue === WHO_ARE_YOU.Guardian && <div className="w-full">
                      <Controller name="studentPhoneNumber" control={control} rules={{
                  pattern: {
                    value: new RegExp(PATTERN.phone),
                    message: 'Please enter a valid mobile number'
                  },
                  minLength: {
                    value: 10,
                    message: 'Minimum 10 digits'
                  },
                  maxLength: {
                    value: 10,
                    message: 'Maximum 10 digits'
                  }
                }} render={({
                  field
                }) => <InputField {...field} type="number" label="Student Phone Number (Optional)" placeholder="Student Phone Number" error={shouldShowError('studentPhoneNumber')} message={shouldShowError('studentPhoneNumber') ? errors.studentPhoneNumber?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth maxLength={10} />} />
                    </div>}
                </div>

                {/* CheckBox */}
                <div className="flex flex-col md:!flex-row gap-4">
                  <div className="hidden w-full md:!block" />
                  <div className="flex items-center w-full ">
                    <Checkbox checkedStyle="icon" disabled={registrationDetails?.isPaid} onChange={e => {
                  if (e.target.checked) {
                    setValue('whatsappNumber', watch('phoneNumber'), {
                      shouldValidate: true
                    });
                  } else {
                    setValue('whatsappNumber', '', {
                      shouldValidate: true
                    });
                  }
                }} checked={parseInt(whatsappNumberValue, 10) === parseInt(phoneNumberValue, 10)} />
                    <Typography variant="regular" component="div" className="!text-xs text-gray-650">
                      Whatsapp Number same as Phone Number
                    </Typography>
                  </div>
                </div>
                {registrationDetails?.tenthPercentage ? <>
                    {/* Who are you? */}
                    <div className="flex flex-col md:!flex-row gap-4">
                      <div className="w-full">
                        <Controller name="whoAreYou" control={control} rules={{
                    required: {
                      value: true,
                      message: 'Please select an option'
                    }
                  }} render={({
                    field
                  }) => <div className="flex gap-3 border border-[#b7b7b7] p-2 rounded-md">
                              <label className="text-[20px] font-semibold">Who are you? *</label>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="radio" value={WHO_ARE_YOU.Student} checked={field.value === WHO_ARE_YOU.Student} onChange={e => field.onChange(e.target.value)} className="w-4 h-4 cursor-pointer accent-blue-600" />
                                  <span className="font-semibold">{WHO_ARE_YOU.Student}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="radio" value={WHO_ARE_YOU.Guardian} checked={field.value === WHO_ARE_YOU.Guardian} onChange={e => field.onChange(e.target.value)} className="w-4 h-4 cursor-pointer accent-blue-600" />
                                  <span className="font-semibold">{WHO_ARE_YOU.Guardian}</span>
                                </label>
                              </div>
                              {shouldShowError('whoAreYou') && <span className="text-sm text-red-600">
                                  {errors.whoAreYou?.message as string}
                                </span>}
                            </div>} />
                      </div>
                    </div>
                  </> : <></>}
                {/* Competitive Exam Details */}
                {compititionExam?.length ? <CompetitiveExamDetails compititionExam={compititionExam} /> : null}

                {/* Work Experience - Only for skipCeeExam courses */}
                {selectedCourseData?.skipCeeExam && <>
                    <div className="flex items-center gap-3">
                      <Controller name="hasWorkExperience" control={control} render={({
                  field
                }) => <div className="flex items-center gap-2">
                            <ToggleButton checked={!!field.value} onChange={e => {
                    field.onChange(e.target.checked);
                    if (!e.target.checked) {
                      setValue('organizationName', '', {
                        shouldValidate: true
                      });
                      setValue('designation', '', {
                        shouldValidate: true
                      });
                      setValue('totalExperienceYears', '', {
                        shouldValidate: true
                      });
                      setValue('annualCtc', '', {
                        shouldValidate: true
                      });
                    }
                  }} disabled={registrationDetails?.isPaid} />
                            <Typography variant="regular" weight="semi-bold" className="!text-sm">
                              I have work experience
                            </Typography>
                          </div>} />
                    </div>

                    {hasWorkExperienceValue && <>
                        <div className="flex flex-col md:!flex-row gap-4">
                          <div className="w-full">
                            <Controller name="organizationName" control={control} rules={{
                      required: {
                        value: true,
                        message: 'Please enter organization name'
                      },
                      minLength: {
                        value: 2,
                        message: 'Minimum 2 characters required'
                      },
                      validate: value => !/^\d+$/.test((value || '').trim()) || 'Organization name cannot be only numbers'
                    }} render={({
                      field
                    }) => <InputField {...field} label="Organization Name *" placeholder="Enter Organization Name" error={shouldShowError('organizationName')} message={shouldShowError('organizationName') ? errors.organizationName?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                          </div>
                          <div className="w-full">
                            <Controller name="designation" control={control} rules={{
                      required: {
                        value: true,
                        message: 'Please enter designation'
                      },
                      minLength: {
                        value: 2,
                        message: 'Minimum 2 characters required'
                      },
                      validate: value => !/^\d+$/.test((value || '').trim()) || 'Designation cannot be only numbers'
                    }} render={({
                      field
                    }) => <InputField {...field} label="Designation *" placeholder="Enter Designation" error={shouldShowError('designation')} message={shouldShowError('designation') ? errors.designation?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                          </div>
                        </div>
                        <div className="flex flex-col md:!flex-row gap-4">
                          <div className="w-full">
                            <Controller name="totalExperienceYears" control={control} rules={{
                      required: {
                        value: true,
                        message: 'Please enter total experience'
                      },
                      validate: val => {
                        if (Number.isNaN(Number(val))) {
                          return 'Please enter a valid number';
                        }
                        if (Number(val) < 0) {
                          return 'Experience cannot be negative';
                        }
                        return true;
                      }
                    }} render={({
                      field
                    }) => <InputField {...field} type="text" label="Total Duration in Months *" placeholder="Enter Total Experience in Months" error={shouldShowError('totalExperienceYears')} message={shouldShowError('totalExperienceYears') ? errors.totalExperienceYears?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                          </div>
                          <div className="w-full">
                            <Controller name="annualCtc" control={control} rules={{
                      required: {
                        value: true,
                        message: 'Please enter annual CTC'
                      },
                      validate: val => {
                        if (Number.isNaN(Number(val))) {
                          return 'Please enter a valid number';
                        }
                        if (Number(val) < 0) {
                          return 'CTC cannot be negative';
                        }
                        return true;
                      }
                    }} render={({
                      field
                    }) => <InputField {...field} type="text" label="Annual CTC (in Lakhs) *" placeholder="Enter Annual CTC" error={shouldShowError('annualCtc')} message={shouldShowError('annualCtc') ? errors.annualCtc?.message as string : ''} disabled={registrationDetails?.isPaid} fullWidth />} />
                          </div>
                        </div>
                      </>}
                  </>}

                <hr />
                {selectedCourseData?.metaData?.note ? <Typography variant="regular" weight="medium">
                    Note: {selectedCourseData?.metaData?.note}
                  </Typography> : null}

                {/* Declaration Checkbox - Not part of form validation */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox checkedStyle="icon" disabled={registrationDetails?.isPaid} checked={registrationDetails?.isPaid ? true : isDeclarationChecked} onChange={e => {
                  if (!registrationDetails?.isPaid) {
                    setIsDeclarationChecked(e.target.checked);
                  }
                }} />
                    <Typography variant="regular" component="div" className="!text-sm text-gray-650">
                      I hereby declare that the information provided by me is true, and I understand
                      it cannot be changed at any later stage.
                    </Typography>
                  </div>
                  {!isDeclarationChecked && <Typography variant="regular" className="!text-xs !text-red-600 ml-8">
                      Please accept the declaration to proceed
                    </Typography>}
                </div>
              </> : null}
          </div>

          <div className="h-8" />
          <div className="py-2">
            <FooterPortal label="Save & Continue" type="submit" disabled={!isValid || registrationDetails?.tenthPercentage && !isDeclarationChecked} btnWrapperClass="relative sm:left-[30%]" formId="basic-details-form" isFormSubmitting={isFormSubmitting || isFetchingEligibility} data-sentry-element="FooterPortal" data-sentry-source-file="index.tsx" />
          </div>
        </form>
      </FormProvider>

      {/* Course Selection Dialog */}
      <CourseSelection isOpen={showCourseSelectionDialog} onClose={handleCourseSelection} closeOnOutsideClick={false} data-sentry-element="CourseSelection" data-sentry-source-file="index.tsx" />

      {/* UnEligibility Dialog */}
      <DialogModal isOpen={showUnEligibleDialog} title="Eligibility Criteria Not Met" onClose={() => {
      setShowUnEligibleDialog(false);
    }} onSubmit={() => {
      setShowUnEligibleDialog(false);
    }} showHeader={false} showSeparator={false} showFooter={false} showCancelButton={false} showSubmitButton={false} showCloseIcon={false} size="extra-small" data-sentry-element="DialogModal" data-sentry-source-file="index.tsx">
        <div className="w-full h-auto">
          <div className="flex flex-col gap-2 text-center">
            <div className="flex justify-center w-full">
              <Image src="/images/registerPage/caution.png" alt="caution" width={100} height={100} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
            </div>
            <Typography variant="heading3" weight="semi-bold" className="" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
              Eligibility Criteria Not Met
            </Typography>
            <Typography variant="regular" className=" text-[#3D3D3D]" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
              We're sorry, but your Class {+tenthPercentageValue <= 40 ? '10th' : '12th'} marks are
              below the required minimum of 40%. Unfortunately, you do not meet the eligibility
              criteria to proceed with the application.
            </Typography>
          </div>
        </div>
      </DialogModal>

      {/* Admission Closed Dialog */}
      <AdmissionClosedModal isOpen={showAdmissionClosedDialog} onClose={() => {
      setShowAdmissionClosedDialog(false);
    }} data-sentry-element="AdmissionClosedModal" data-sentry-source-file="index.tsx" />
    </section>;
};
export default BasicDetailsForm;